import type {
  AxisType,
  RawDataItem,
  StackableDataset,
  StackableStyleConfig,
  StackableTransform,
} from "@/types/ChartData";
import { get, groupBy, map as lodashMap, range } from "lodash-es";
import { computed, ComputedRef, Ref } from "vue";
import { useI18n } from "vue-i18n";
import { naturalCollator, stackValueLabel } from "./filterableChartUtils";
import { formatShortMonthYear } from "./formatUtils";

/**
 * Formats a Date object into "MMM YYYY" format (e.g., "Feb 2025").
 */
function formatDateLabel(date: Date): string {
  return formatShortMonthYear(date) || "Invalid Date";
}

/**
 * Months since year 0, so consecutive months differ by exactly one and the months between
 * two values are a plain numeric range. Read in UTC: date-only strings such as "2025-01"
 * parse as UTC midnight, which local-time getters would shift into the previous month
 * anywhere west of Greenwich.
 */
function monthOrdinal(date: Date): number {
  return date.getUTCFullYear() * 12 + date.getUTCMonth();
}

/** First day of the month in local time, matching how `formatShortMonthYear` renders dates. */
function monthOrdinalToDate(ordinal: number): Date {
  return new Date(Math.floor(ordinal / 12), ordinal % 12, 1);
}

/**
 * Bucket of the items without an x value on a string or number axis: labelled "Unknown" and
 * placed last, so the bars add up to every row the chart counts. "null" is also the key such
 * items stack under (see `createStackOption`). A date axis is a timeline with no slot for
 * them, so there they stay out of the chart.
 */
const UNKNOWN_BUCKET_KEY = "null";

/**
 * Key under which an x value is aggregated: the month ordinal on date axes, the normalised
 * value otherwise, and {@link UNKNOWN_BUCKET_KEY} for a missing value. `undefined` for values
 * the axis cannot place; their items stay out of the chart.
 */
function toBucketKey(xValue: any, axisType: AxisType): string | undefined {
  if (xValue === null || xValue === undefined) {
    return axisType === "date" ? undefined : UNKNOWN_BUCKET_KEY;
  }
  if (axisType === "date") {
    const date = new Date(xValue);
    return isNaN(date.getTime()) ? undefined : String(monthOrdinal(date));
  }
  if (axisType === "number") {
    const num = Number(xValue);
    return isNaN(num) ? undefined : String(num);
  }
  return String(xValue);
}

/**
 * One x-axis position: the label the chart shows and the bucket key (see `toBucketKey`)
 * shared by the items summed under it. A filled-in gap month has a key that no item carries.
 */
interface XAxisBucket {
  key: string;
  label: string;
}

const DEFAULT_STYLE = {
  backgroundColor: "#CCCCCC",
  borderColor: "#AAAAAA",
  borderWidth: 0,
};

/**
 * Longest month span a date axis fills in. Real portfolios span a few years; a wider span
 * means a corrupt production date (an epoch default, a mistyped year), and filling it would
 * only allocate thousands of empty bars. Past the limit the axis falls back to the months
 * that have data.
 */
const MAX_FILLED_MONTHS = 240;

/**
 * A Vue 3 composable function that transforms raw data into a format suitable
 * for stacked charts, applying aggregation and styling according to the provided
 * configuration. Callers pass rows that are already filtered.
 *
 * A "date" x-axis is a month timeline: it spans every month from the earliest to the
 * latest value in the data, so months without data render as empty slots
 * instead of being skipped.
 *
 * @template T - The type of the raw data items, extending Record<string, any>.
 * @param rawData - A Ref containing the array of raw data objects.
 * @param transformConfig - A Ref containing the configuration for the transformation.
 * @returns An object containing computed refs for the stackable dataset and unique x-axis values.
 */
export function useStackableDataTransformer(
  rawData: Ref<ReadonlyArray<RawDataItem>>,
  transformConfig: Ref<StackableTransform>
): {
  stackableDataset: ComputedRef<StackableDataset[]>;
  uniqueXValues: ComputedRef<string[]>; // Always returns formatted strings
} {
  const { t } = useI18n();

  const xAxisType = computed(
    (): AxisType => transformConfig.value.xAxisOption.axisType || "string"
  );

  // --- Helper Functions for Label Generation ---
  // Label of a string or number bucket, prioritizing translation, then custom
  // formatting, then the bucket key itself. `xValue` is the first raw value seen in the bucket.
  const generateXAxisLabel = (key: string, xValue: any): string => {
    if (key === UNKNOWN_BUCKET_KEY) return t("common.unknown");
    const { valueTranslationKeyPrefix, labelFormat } =
      transformConfig.value.xAxisOption;
    if (valueTranslationKeyPrefix) {
      return t(`${valueTranslationKeyPrefix}.${xValue}`, String(xValue));
    }
    if (labelFormat) {
      return labelFormat(xValue);
    }
    return key;
  };

  // Label of a month on a date axis. Months are labelled from their own start date rather
  // than from a raw value, so filled-in gap months, which have none, label the same way.
  const generateMonthLabel = (monthStart: Date): string => {
    const { labelFormat } = transformConfig.value.xAxisOption;
    return labelFormat ? labelFormat(monthStart) : formatDateLabel(monthStart);
  };

  // Distinct x values of the items, each mapped to the bucket it aggregates under.
  const bucketKeyByXValue = computed((): Map<any, string> => {
    const { fieldName } = transformConfig.value.xAxisOption;
    const map = new Map<any, string>();
    if (!fieldName) return map;

    for (const item of rawData.value) {
      const xValue = get(item, fieldName);
      if (map.has(xValue)) continue;
      const key = toBucketKey(xValue, xAxisType.value);
      if (key !== undefined) map.set(xValue, key);
    }
    return map;
  });

  // Ordered x-axis positions. A date axis spans every month from the earliest to the
  // latest value, so a month without data is an empty slot rather than a skipped one.
  const xAxisBuckets = computed((): XAxisBucket[] => {
    const keyByXValue = bucketKeyByXValue.value;
    if (keyByXValue.size === 0) return [];

    if (xAxisType.value === "date") {
      const presentOrdinals = [
        ...new Set([...keyByXValue.values()].map(Number)),
      ].sort((a, b) => a - b);
      const first = presentOrdinals[0];
      const last = presentOrdinals[presentOrdinals.length - 1];
      const ordinals =
        last - first + 1 <= MAX_FILLED_MONTHS
          ? range(first, last + 1)
          : presentOrdinals;
      return ordinals.map((ordinal) => ({
        key: String(ordinal),
        label: generateMonthLabel(monthOrdinalToDate(ordinal)),
      }));
    }

    // The first x value seen in a bucket supplies its translated or custom label.
    const xValueByKey = new Map<string, any>();
    for (const [xValue, key] of keyByXValue) {
      if (!xValueByKey.has(key)) xValueByKey.set(key, xValue);
    }
    const compareKeys =
      xAxisType.value === "number"
        ? (a: string, b: string) => Number(a) - Number(b)
        : naturalCollator.compare;
    const knownKeys = [...xValueByKey.keys()]
      .filter((key) => key !== UNKNOWN_BUCKET_KEY)
      .sort(compareKeys);
    const keys = xValueByKey.has(UNKNOWN_BUCKET_KEY)
      ? [...knownKeys, UNKNOWN_BUCKET_KEY]
      : knownKeys;
    return keys.map((key) => ({
      key,
      label: generateXAxisLabel(key, xValueByKey.get(key)),
    }));
  });

  const uniqueXValues = computed((): string[] =>
    xAxisBuckets.value.map((bucket) => bucket.label)
  );

  const stackableDataset = computed((): StackableDataset[] => {
    const { xAxisOption, yAxis, stackOption } = transformConfig.value;
    const xAxis = xAxisOption.fieldName;
    const stackBy = stackOption.fieldName;
    const stackStyleMap = stackOption.styleMap;

    if (!xAxis || !yAxis || !stackBy) return [];

    // Items the axis can place, each paired with its bucket. Items without a bucket (an
    // unparseable x value, or a missing one on a date axis) take no part, so a stack made up
    // only of them gets no legend entry.
    const keyByXValue = bucketKeyByXValue.value;
    const placedItems = rawData.value.flatMap((item) => {
      const key = keyByXValue.get(get(item, xAxis));
      return key === undefined ? [] : [{ item, key }];
    });
    if (!placedItems.length) return [];

    const buckets = xAxisBuckets.value;
    const groupedData = groupBy(placedItems, ({ item }) => get(item, stackBy));

    const datasets: StackableDataset[] = lodashMap(
      groupedData,
      (placedInStack, stackValue) => {
        // Sum the y values per bucket. A bucket with no items, such as a gap month, stays at zero.
        const sumByBucket = new Map<string, number>();
        for (const { item, key } of placedInStack) {
          const value = Number(get(item, yAxis)) || 0;
          sumByBucket.set(key, (sumByBucket.get(key) ?? 0) + value);
        }
        const dataForStack = buckets.map(
          (bucket) => sumByBucket.get(bucket.key) ?? 0
        );

        const styleConfig = stackStyleMap?.[stackValue] || {};
        const finalStyle: Required<StackableStyleConfig> = {
          backgroundColor:
            styleConfig.backgroundColor || DEFAULT_STYLE.backgroundColor,
          borderColor:
            styleConfig.borderColor ||
            styleConfig.backgroundColor ||
            DEFAULT_STYLE.borderColor,
          borderWidth: styleConfig.borderWidth ?? DEFAULT_STYLE.borderWidth,
        };

        if (!stackStyleMap?.[stackValue]) {
          console.warn(
            `[useStackableDataTransformer] No style found for stack value "${stackValue}". Applying default style.`
          );
        }

        return {
          label: stackValueLabel(stackOption, stackValue, t),
          data: dataForStack,
          ...finalStyle,
        };
      }
    );

    // Sort datasets by label using the natural collator
    return datasets.sort((a, b) => naturalCollator.compare(a.label, b.label));
  });

  return {
    stackableDataset,
    uniqueXValues,
  };
}
